// WeekPlanContent.jsx
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useKeycloak } from "@react-keycloak/web";
import {Form, Input, Button, InputNumber, Switch, Tooltip, Modal, Space, message } from "antd";
import { SaveOutlined, CheckCircleOutlined, CloseCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import styles from "./WeekPlan.module.css";
import { getUserWeekPlanInOrgByWeekAndYear, saveUserWeekPlan, deleteUserWeekPlan } from "../../redux/slieces/weekPlanSlice";
import { getCurrentSubOrganisations } from "../../redux/slieces/subOrganisationSlice";
import { getUsersInOrganisation } from "../../redux/slieces/userSlice";
import { getOrganizationSettings } from "../../redux/slieces/organizationSettingsSlice";
import { WeekPlanCard } from "./WeekPlanCard";
import { sortWorkers } from "../../utils/workerSorting";
import { getDateFromWeekAndDay } from "../../core/utils/weekPlanUtils";
import {
  setWorkDurationTime,
  setDayStatus,
  setComment,
  setSubOrganisation,
  addWorkDuration,
  removeWorkDuration,
} from "../../core/weekplan/weekPlanEditing";
import notificationBus from "../../events/notificationBus";
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

// Extend dayjs with UTC support
dayjs.extend(utc);

const WeekPlanContent = ({ selectedWeek, selectedYear, weekPlanSettings, onWeekChange }) => {
  const dispatch = useDispatch();
  const { initialized, keycloak } = useKeycloak();
  const { loading, error, weekPlanSchedules } = useSelector(state => state.weekPlan.WeekPlanData);
  const { subOrganisations } = useSelector(state => state.subOrganisationData);
  const { users } = useSelector(state => state.userData.users);
  const organizationSettings = useSelector(state => state.organizationSettings.settings);
  const subOrganisationsData = subOrganisations?.data || [];
  const [currentWeekPlan, setCurrentWeekPlan] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [filteredWeekPlan, setFilteredWeekPlan] = useState([]);
  const [activateAll, setActivateAll] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [showFloatingSaveButton, setShowFloatingSaveButton] = useState(false);
  
  // Local state for input values to allow immediate UI updates
  const [localWeek, setLocalWeek] = useState(selectedWeek);
  const [localYear, setLocalYear] = useState(selectedYear);
  
  const { t } = useTranslation();
  const hasMediAdminRole = keycloak.hasRealmRole('mediadmin');

  // Sync local state with props when they change from external sources (like calendar)
  useEffect(() => {
    setLocalWeek(selectedWeek);
    setLocalYear(selectedYear);
  }, [selectedWeek, selectedYear]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Scroll detection for floating save button
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const shouldShow = scrollTop > 200;
      setShowFloatingSaveButton(shouldShow);
    };

    window.addEventListener('scroll', handleScroll);
    // Also check initial scroll position
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
        console.log('Fetching data for week:', selectedWeek, 'year:', selectedYear);
        setPageLoading(true);
   
     await dispatch(getUserWeekPlanInOrgByWeekAndYear({ week: selectedWeek, year: selectedYear }));
     // Fetch sub-organisations for dropdown selection
     await dispatch(getCurrentSubOrganisations());
     // Fetch users for name formatting
     await dispatch(getUsersInOrganisation());
     // Fetch organization settings for sorting
     await dispatch(getOrganizationSettings());
        setPageLoading(false);

    };
    
    if (initialized && selectedWeek && selectedYear) {
        fetchData();
    }
  }, [dispatch, initialized, selectedWeek, selectedYear]);

  useEffect(() => {
    setCurrentWeekPlan(weekPlanSchedules || []);
  }, [weekPlanSchedules]);

  useEffect(() => {
    const filtered = currentWeekPlan.filter(wp => {
      const name = wp.userName?.toLowerCase() || "";
      if (searchName.trim() === "") return true;
      return name.includes(searchName.toLowerCase().trim());
    });
    // Apply sorting to filtered results
    const sorted = sortWorkers(filtered, users, organizationSettings);
    setFilteredWeekPlan(sorted);
  }, [searchName, currentWeekPlan, users, organizationSettings]);

  const handleActivateAll = (checked) => {
    setActivateAll(checked);
    setCurrentWeekPlan(prevPlans => prevPlans.map(plan => ({
      ...plan,
      calendarEmployees: plan.calendarEmployees.map(calendar => ({
        ...calendar,
        activate: checked
      }))
    })));
  };

  const handleSaveChanges = async () => {
    // Activation is an admin-only action. Regular users can only save their
    // changes, so skip the activate/save confirmation dialog and save directly
    // (preserving any existing activation state round-tripped from the server).
    if (!hasMediAdminRole) {
      console.log('Save Changes clicked (non-admin) - saving without activation');
      await performSave();
      return;
    }
    console.log('Save Changes clicked - showing confirmation dialog');
    setShowSaveConfirmModal(true);
  };

  const handleConfirmSaveWithActivation = async () => {
    console.log('User clicked Yes - activating all and saving');
    setShowSaveConfirmModal(false);
    
    // Create the activated data directly instead of relying on state updates
    const activatedWeekPlan = currentWeekPlan.map(plan => ({
      ...plan,
      userName: plan.userName || "",
      calendarEmployees: plan.calendarEmployees.map(calendar => ({
        ...calendar,
        activate: true
      }))
    }));
    
    // Update state and save with activated data
    setCurrentWeekPlan(activatedWeekPlan);
    await dispatch(saveUserWeekPlan({ userWeekPlan: activatedWeekPlan, week: selectedWeek, year: selectedYear }));
    // Re-fetch from the server so the view reflects the authoritative saved/
    // activated state (the backend may normalise the plan), rather than leaving
    // the UI on the optimistic/echoed save response. Mirrors the restore handler.
    await dispatch(getUserWeekPlanInOrgByWeekAndYear({ week: selectedWeek, year: selectedYear }));
    notificationBus.emit('notify', {
      type: 'success',
      message: t('weekPlanSavedSuccessfully'),
      description: t('weekPlanSavedSuccessfullyDescription'),
    });
  };

  const handleConfirmSaveWithoutActivation = async () => {
    console.log('User clicked No - just saving');
    setShowSaveConfirmModal(false);
    await performSave();
  };

  const handleCancelSave = () => {
    console.log('User cancelled save');
    setShowSaveConfirmModal(false);
  };

  const performSave = async () => {
    setCurrentWeekPlan(prevPlans => prevPlans.map(plan => ({
      ...plan,
      userName: plan.userName || "",
    })));

    await dispatch(saveUserWeekPlan({ userWeekPlan: currentWeekPlan, week: selectedWeek, year: selectedYear }));
    // Re-fetch so the view reflects the authoritative server state after save
    // (see handleConfirmSaveWithActivation / handleDeleteUserWeekPlan).
    await dispatch(getUserWeekPlanInOrgByWeekAndYear({ week: selectedWeek, year: selectedYear }));
    notificationBus.emit('notify', {
        type: 'success',
        message: t('weekPlanSavedSuccessfully'),
        description: t('weekPlanSavedSuccessfullyDescription'),
      });
  };

  const handleTimeChange = (userId, day, durationIndex, type, timeString) => {
    setCurrentWeekPlan(prev =>
      setWorkDurationTime(prev, userId, day, durationIndex, type, timeString, selectedYear, selectedWeek));
  };

  const updateDayStatus = (userId, day, newStatus) => {
    setCurrentWeekPlan(prev => setDayStatus(prev, userId, day, newStatus));
  };

  const handleActivateChanges = (userId, week, year, checked) => {
    setCurrentWeekPlan(prevPlans => prevPlans.map(plan => {
      if (plan.userId !== userId) return plan;
      return {
        ...plan,
        calendarEmployees: plan.calendarEmployees.map(calendar => ({
          ...calendar,
          activate: checked
        }))
      };
    }));
  };

  const handleSubOrganisationChange = (userId, day, subOrganisationId) => {
    setCurrentWeekPlan(prev => setSubOrganisation(prev, userId, day, subOrganisationId, subOrganisationsData));
  };

  const handleCommentChange = (userId, day, comment) => {
    setCurrentWeekPlan(prev => setComment(prev, userId, day, comment));
  };

  const handleAddWorkDuration = (userId, day) => {
    try {
      const currentPlan = currentWeekPlan.find(plan => plan.userId === userId);
      if (!currentPlan || !currentPlan.calendarEmployees || !currentPlan.calendarEmployees[0]) {
        message.error(t("errorUserNotFound") || "User not found");
        return;
      }
      const dailyPlan = currentPlan.calendarEmployees[0].dailyPlanEmployees.find(dp => dp.day === day);
      if (!dailyPlan) {
        message.error(t("errorDayNotFound") || "Day not found");
        return;
      }
      setCurrentWeekPlan(prev => addWorkDuration(prev, userId, day, selectedYear, selectedWeek));
      message.success(t("workDurationAdded") || "Work duration added successfully");
    } catch (error) {
      console.error("Error adding work duration:", error);
      message.error(t("errorAddingWorkDuration") || "Error adding work duration");
    }
  };

  const handleRemoveWorkDuration = (userId, day, durationIndex) => {
    try {
      const currentPlan = currentWeekPlan.find(plan => plan.userId === userId);
      if (!currentPlan || !currentPlan.calendarEmployees || !currentPlan.calendarEmployees[0]) {
        message.error(t("errorUserNotFound") || "User not found");
        return;
      }
      const dailyPlan = currentPlan.calendarEmployees[0].dailyPlanEmployees.find(dp => dp.day === day);
      if (!dailyPlan) {
        message.error(t("errorDayNotFound") || "Day not found");
        return;
      }
      const existingDurations = dailyPlan.workDurationEmployees || [];
      if (existingDurations.length <= 1) {
        message.warning(t("cannotDeleteLastDuration") || "Cannot delete the last work duration");
        return;
      }
      if (durationIndex < 0 || durationIndex >= existingDurations.length) {
        message.error(t("invalidDurationIndex") || "Invalid work duration");
        return;
      }
      setCurrentWeekPlan(prev => removeWorkDuration(prev, userId, day, durationIndex));
      message.success(t("workDurationRemoved") || "Work duration removed successfully");
    } catch (error) {
      console.error("Error removing work duration:", error);
      message.error(t("errorRemovingWorkDuration") || "Error removing work duration");
    }
  };

  const updateCurrentWeekPlan = (userId, updater) => {
    setCurrentWeekPlan(prevPlans => prevPlans.map(plan =>
      plan.userId === userId ? updater(plan) : plan
    ));
  };

  const handleDeleteUserWeekPlan = async (userId, week, year) => {
    await dispatch(deleteUserWeekPlan({ userId, week, year }));
    // Re-fetch so the restored (template-regenerated) week plan shows without a
    // manual browser refresh. The DELETE makes the backend regenerate the plan
    // from the template but returns no schedule data, so — unlike save/activate
    // whose responses update `weekPlanSchedules` — nothing in the store changes
    // here. Re-dispatch the same load thunk the page uses (mirrors the load
    // useEffect) to pull the regenerated plan into Redux → currentWeekPlan.
    await dispatch(getUserWeekPlanInOrgByWeekAndYear({ week: selectedWeek, year: selectedYear }));

    notificationBus.emit("notify", { type: "success", message: "Week plan deleted successfully" });
  };

  const handleWeekChange = (newWeek) => {
    console.log('handleWeekChange called with:', newWeek);
    console.log('Current localYear:', localYear);
    console.log('onWeekChange available:', !!onWeekChange);
    
    // Update local state immediately for UI responsiveness
    setLocalWeek(newWeek);
    
    // Skip validation for null/undefined (clearing input)
    if (newWeek === null || newWeek === undefined) {
      console.log('Skipping - newWeek is null/undefined');
      return;
    }
    
    if (!onWeekChange) {
      console.log('Skipping - no onWeekChange callback');
      return;
    }
    
    console.log('Validating week range:', newWeek, '>=1 and <=53:', (newWeek >= 1 && newWeek <= 53));
    
    // Validate and propagate changes
    if (newWeek >= 1 && newWeek <= 53) {
      // Calculate max weeks correctly for the selected year
      // Use December 28th which is always in the last week of the year
      const maxWeeks = dayjs(`${localYear}-12-28`).isoWeek();
      console.log('Max weeks for year', localYear, ':', maxWeeks);
      console.log('Week validation:', newWeek, '<=', maxWeeks, ':', (newWeek <= maxWeeks));
      
      if (newWeek <= maxWeeks) {
        console.log('Valid week, updating parent with:', { year: localYear, week: newWeek });
        onWeekChange({ year: localYear, week: newWeek });
      } else {
        // Show error but keep the input value for user to correct
        console.log('Week exceeds max weeks for year');
        message.error(`${t('invalidWeekNumber')} ${newWeek}. ${t('maxWeeksInYear')} ${localYear}: ${maxWeeks}`);
      }
    } else {
      // Show error for out of range values but keep the input
      console.log('Week out of valid range (1-53)');
      message.error(`${t('invalidWeekNumber')} ${newWeek}. ${t('validWeekRange')}: 1-53`);
    }
  };

  const handleYearChange = (newYear) => {
    console.log('handleYearChange called with:', newYear);
    
    // Update local state immediately for UI responsiveness
    setLocalYear(newYear);
    
    // Skip validation for null/undefined (clearing input)
    if (newYear === null || newYear === undefined || !onWeekChange) {
      return;
    }
    
    // Validate and propagate changes
    if (newYear >= 2000 && newYear <= 2100) {
      // Check if current week is valid for new year
      // Use December 28th which is always in the last week of the year
      const maxWeeks = dayjs(`${newYear}-12-28`).isoWeek();
      if (localWeek > maxWeeks) {
        // Adjust to last week of new year if current week is invalid
        message.warning(`${t('weekAdjustedForYear')} ${newYear}. ${t('maxWeeksInYear')} ${newYear}: ${maxWeeks}`);
        setLocalWeek(maxWeeks);
        console.log('Valid year with adjusted week, updating parent with:', { year: newYear, week: maxWeeks });
        onWeekChange({ year: newYear, week: maxWeeks });
      } else {
        console.log('Valid year, updating parent with:', { year: newYear, week: localWeek });
        onWeekChange({ year: newYear, week: localWeek });
      }
    } else if (newYear !== null && newYear !== undefined) {
      // Show error for invalid year range but keep the input
      message.error(`${t('invalidYear')} ${newYear}. ${t('validYearRange')}: 2000-2100`);
    }
  };

 // if (!initialized) return <div>Loading authentication...</div>;
 // if (loading) return <div>Loading week plan...</div>;
 // if (error) return <div>Error loading week plan: {error}</div>;

  return (
    <div className={styles.container}>
        
        <Form
  layout="inline"
  onFinish={handleSaveChanges} // wird bei Enter oder Button-Klick ausgelöst
  className={styles.inputRow}
>
        <Form.Item label={t("calenderWeek")}>
            <InputNumber
            min={1}
            max={53}
            value={localWeek}
            onChange={handleWeekChange}
            placeholder="Week"
            />
        </Form.Item>

        <Form.Item label={t("year")}>
            <InputNumber
            min={2000}
            max={2100}
            value={localYear}
            onChange={handleYearChange}
            placeholder="Year"
            />
        </Form.Item>

        <Form.Item>
            <Button type="primary" htmlType="submit">
            {t("saveChanges")}
            </Button>
        </Form.Item>
        </Form>


      <div className={styles.searchRow}>
        <div className={styles.search}>
          <Input
            placeholder={t("searchEmployee")}
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
          />
        </div>

        {hasMediAdminRole && (
          <Tooltip title={t("activateAllWeekPlans")}>
            <div className={styles.toggleSwitch}>
              <label>{t("activateAllWeekPlans")}</label>
              <Switch
                checked={activateAll}
                onChange={handleActivateAll}
              />
            </div>
          </Tooltip>
        )}
      </div>
{ pageLoading ? (<div className={styles.loader}>
       <div className={styles.spinner}></div>
       <p>{t("loadingEmployeeTemplates")}...</p>
     </div>):
      (filteredWeekPlan.map((wp, index) => (
        <WeekPlanCard
          key={wp.userId || wp.id || `no-username-${index}`}
          currentWeekPlan={wp}
          currentCulture="de-DE"
          updateCurrentWeekPlan={updateCurrentWeekPlan}
          deleteUserWeekPlan={handleDeleteUserWeekPlan}
          selectedWeek={selectedWeek}
          selectedYear={selectedYear}
          handleTimeChange={handleTimeChange}
          updateDayStatus={updateDayStatus}
          isMobile={isMobile}
          handleActivateChanges={handleActivateChanges}
          handleCommentChange={handleCommentChange}
          handleSubOrganisationChange={handleSubOrganisationChange}
          subOrganisations={subOrganisationsData}
          hasMediAdminRole={hasMediAdminRole}
          weekPlanSettings={weekPlanSettings}
          users={users}
          handleAddWorkDuration={handleAddWorkDuration}
          handleRemoveWorkDuration={handleRemoveWorkDuration}
        />
      )))
}
      {/* Save Confirmation Modal */}
      <Modal
        title={
          <Space>
            <SaveOutlined style={{ color: '#1890ff' }} />
            {t('confirmSaveWeekPlan') || 'Save Week Plan'}
          </Space>
        }
        open={showSaveConfirmModal}
        onCancel={handleCancelSave}
        centered
        width={680}
        maskClosable={false}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <Button 
              onClick={handleCancelSave}
              icon={<CloseCircleOutlined />}
              size="large"
            >
              {t('cancel') || 'Cancel'}
            </Button>
            <Button 
              onClick={handleConfirmSaveWithoutActivation}
              icon={<SaveOutlined />}
              size="large"
              style={{ 
                backgroundColor: '#faad14', 
                borderColor: '#faad14',
                color: 'white'
              }}
            >
              {t('noJustSave') || 'No, Just Save'}
            </Button>
            <Button 
              type="primary" 
              onClick={handleConfirmSaveWithActivation}
              icon={<CheckCircleOutlined />}
              size="large"
              style={{
                backgroundColor: '#52c41a',
                borderColor: '#52c41a'
              }}
            >
              {t('yesActivateAndSave') || 'Yes, Activate & Save'}
            </Button>
          </div>
        }
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: '16px',
            marginBottom: '20px'
          }}>
            <QuestionCircleOutlined 
              style={{ 
                fontSize: '24px', 
                color: '#1890ff', 
                marginTop: '2px',
                flexShrink: 0
              }} 
            />
            <div>
              <p style={{ 
                fontSize: '16px', 
                fontWeight: '500',
                margin: '0 0 8px 0',
                color: '#262626'
              }}>
                {t('confirmActivateAllWeekPlansBeforeSaving') || 'Do you want to activate all week plans for the selected calendar week before saving?'}
              </p>
              <p style={{ 
                fontSize: '14px', 
                color: '#8c8c8c',
                margin: 0,
                lineHeight: '1.5'
              }}>
                {t('activateAllWeekPlansExplanation') || 'Activating all week plans will enable them for the selected week and make them visible in the Schedule Viewer. You can also save without activating if you prefer to enable them individually later.'}
              </p>
            </div>
          </div>
        </div>
      </Modal>

      {/* Floating Save Button using Portal - renders directly to body */}
      {showFloatingSaveButton && createPortal(
        <Button
          type="primary"
          onClick={handleSaveChanges}
          className={`${styles.floatingSaveButton} ${styles.floatingSaveButtonVisible}`}
          icon={<SaveOutlined />}
          size="large"
          aria-label={t("saveChanges")}
        >
          {!isMobile && t("saveChanges")}
        </Button>,
        document.body
      )}
    </div>
  );
};

export default WeekPlanContent;
